import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import './index.css';

// Providers
import { ErrorProvider } from './contexts/ErrorContext';

// Analytics
import { initGA } from './utils/analytics';
import { usePageTracking } from './hooks/useAnalytics';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import MobileBottomNav from './components/MobileBottomNav';
import OfflineNotification from './components/OfflineNotification';
import Chatbot from './components/Chatbot';

// Auth Provider
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';

// Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import PackageDetailPage from './pages/PackageDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AccountPage from './pages/AccountPage';
import AdminDashboard from './pages/admin/AdminDashboardNew';
import AdminForceAccess from './pages/admin/AdminForceAccess';
import FAQPage from './pages/FAQPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import UserDashboard from './pages/UserDashboard';
import OrderConfirmation from './pages/OrderConfirmation';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import PhonePeTestPage from './pages/PhonePeTestPage';

// Import CartProvider
import { CartProvider } from './contexts/CartContext';

// Lazy Loading
import { enableLazyLoadingOnExistingImages } from './components/LazyLoad';

// Analytics Page Tracking Component
function AnalyticsTracker() {
  usePageTracking();
  return null;
}

function App() {
  console.log('⚛️ [App] Component rendering...');
  
  // Initialize Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  // Enable lazy loading on all images
  useEffect(() => {
    enableLazyLoadingOnExistingImages();
    
    // Re-run when DOM changes (for dynamically added images)
    const observer = new MutationObserver(() => {
      enableLazyLoadingOnExistingImages();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ErrorProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <CartProvider>
              <AnalyticsTracker />
              <ScrollToTop />
              <OfflineNotification />
              <Routes>
                {/* Admin Force Access - For debugging */}
                <Route 
                  path="/admin-fix" 
                  element={
                    <ProtectedRoute>
                      <AdminForceAccess />
                    </ProtectedRoute>
                  } 
                />

                {/* Admin Panel - No Header/Footer */}
                <Route 
                  path="/admin/*" 
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />

                {/* Order Confirmation - No Header/Footer */}
                <Route 
                  path="/order-confirmation/:id" 
                  element={
                  <ProtectedRoute>
                    <OrderConfirmation />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/order-confirmation" 
                element={
                  <ProtectedRoute>
                    <OrderConfirmation />
                  </ProtectedRoute>
                } 
              />

              {/* All other routes with Header and Footer */}
              <Route path="*" element={
                <div className="min-h-screen bg-white md:bg-gray-50">
                  {/* Mobile App-like Container */}
                  <div className="md:max-w-full mx-auto md:bg-white">
                    <Header />
                    <main className="pb-20 md:pb-0 md:min-h-screen">
                      <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/services" element={<ServicesPage />} />
                      <Route path="/services/:id" element={<ServiceDetailPage />} />
                      <Route path="/packages/:id" element={<PackageDetailPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/signup" element={<Signup />} />
                      
                      {/* Legal Pages */}
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/refund" element={<RefundPolicyPage />} />
                      
                      {/* PhonePe Test Payment Page */}
                      <Route path="/phonepe-payment" element={<PhonePeTestPage />} />
                      
                      {/* Protected Routes */}
                      <Route path="/cart" element={
                        <ProtectedRoute>
                          <CartPage />
                        </ProtectedRoute>
                      } />
                      <Route 
                        path="/checkout" 
                        element={
                          <ProtectedRoute>
                            <CheckoutPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/account" 
                        element={
                          <ProtectedRoute>
                            <AccountPage />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute>
                            <UserDashboard />
                          </ProtectedRoute>
                        } 
                      />
                    </Routes>
                  </main>
                  <Footer />
                  <MobileBottomNav />
                  </div>
                </div>
              } />
            </Routes>
          </CartProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ErrorProvider>
      <Chatbot />
    </Router>
  );
}

export default App;
